import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, withI18n } from '@lingui/react';
import { toast } from 'react-toastify';
import { FaTrash, FaEdit } from 'react-icons/fa';

import MatchWhenRole from '../../../components/routing/MatchWhenRole';
import Loader from '../../../components/utils/Loader';
import ArticlePagination from '../../../components/ArticlePagination';
import ProfileEditModal from './modals/ProfileEditModal';
import { useProfiles } from '../hooks/useProfiles';
import ProfilePropertiesBadges from './ProfilePropertiesBadges';
import { deleteProfile } from '../api';
import { setIsLoadingFromLocalStorage, setLoadedFromOthersPages } from '../../../actions/pageStateActions';

const ProfilesList = ({ i18n }) => {
    const dispatch = useDispatch();
    const {
        profiles,
        isFetching,
        fetchProfiles,
        currentPage,
        totalPages,
        totalItems,
        handlePageChange,
    } = useProfiles();

    const [showModal, setShowModal] = useState(false);
    const [currentProfileId, setCurrentProfileId] = useState(null);
    const [deletingProfileId, setDeletingProfileId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        dispatch(setIsLoadingFromLocalStorage(false));
        fetchProfiles(1).catch(console.error);

        return () => {
            dispatch(setLoadedFromOthersPages(true));
        };
    }, [dispatch, fetchProfiles]);

    const handleNew = () => {
        setShowModal(true);
        setCurrentProfileId('new');
    };

    const handleEdit = (id) => {
        setShowModal(true);
        setCurrentProfileId(id);
    };

    const closeModal = () => {
        setShowModal(false);
        setCurrentProfileId(null);
    };

    const handleModalSuccess = async () => {
        setShowModal(false);
        setCurrentProfileId(null);
        await fetchProfiles(currentPage);
    };

    const handleDeleteRequest = (id) => {
        setDeletingProfileId(id);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await deleteProfile(deletingProfileId);
            toast(i18n._('profiles:deleted'), {
                position: 'bottom-right',
                type: toast.TYPE.SUCCESS,
            });
            setDeletingProfileId(null);
            await fetchProfiles(currentPage);
        } catch (err) {
            toast(i18n._('error:unexpected'), {
                position: 'bottom-right',
                type: toast.TYPE.ERROR,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeletingProfileId(null);
    };

    const getCurrentProfile = () => {
        if (currentProfileId === 'new') {
            return { id: null };
        }
        return profiles.find((p) => p.id === currentProfileId);
    };

    const onPageChange = ({ selected }) => {
        handlePageChange(selected + 1);
    };

    const deletingProfile = deletingProfileId
        ? profiles.find((p) => p.id === deletingProfileId)
        : null;

    return (
        <>
            {showModal && (
                <ProfileEditModal
                    profile={getCurrentProfile()}
                    onClose={closeModal}
                    onSuccess={handleModalSuccess}
                />
            )}

            {deletingProfile && (
                <div className="modal active" style={{ zIndex: 1000 }}>
                    <span className="modal-overlay" onClick={handleDeleteCancel} />
                    <div className="modal-container" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <div className="modal-title h5">
                                <Trans id="profiles:confirm:delete:title" />
                                <span className="btn btn-clear float-right" onClick={handleDeleteCancel} />
                            </div>
                        </div>
                        <div className="modal-body">
                            <p><Trans id="profiles:confirm:delete" /></p>
                            <p><strong>{deletingProfile.name}</strong> ({deletingProfile.slug})</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className={`btn btn-error mr-2 ${isDeleting ? 'loading' : ''}`}
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                            >
                                <Trans id="confirm:delete" />
                            </button>
                            <button
                                className="btn"
                                onClick={handleDeleteCancel}
                                disabled={isDeleting}
                            >
                                <Trans id="cancel" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-1">
                <div className="grid grid-cols-12 gap-1">
                    <div className="col-span-full text-center mt-2">
                        <h2 className="text-primary">
                            <Trans id="profiles:title" />
                        </h2>
                    </div>
                    <div className="col-span-full my-2 text-right">
                        <button className="btn btn-primary" onClick={handleNew}>
                            <i className="icon icon-plus" />{' '}
                            <Trans id="profiles:new" />
                        </button>
                    </div>
                    <div className="col-span-full">
                        {isFetching ? (
                            <Loader />
                        ) : (
                            <>
                                <table className="table table-striped table-hover table-dense">
                                    <thead>
                                        <tr>
                                            <th><Trans id="name" /></th>
                                            <th><Trans id="profiles:slug" /></th>
                                            <th><Trans id="profiles:status" /></th>
                                            <th><Trans id="profiles:rules" /></th>
                                            <th style={{ width: '10%' }}><Trans id="actions" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {profiles.map((profile) => (
                                            <tr key={profile.id}>
                                                <td>{profile.name}</td>
                                                <td>
                                                    <code className="text-sm">{profile.slug}</code>
                                                </td>
                                                <td>
                                                    <span className={`label ${profile.enabled ? 'label-success' : 'label-secondary'}`}>
                                                        <Trans id={profile.enabled ? 'enabled' : 'disabled'} />
                                                    </span>
                                                </td>
                                                <td><ProfilePropertiesBadges rules={profile.rules} /></td>
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => handleEdit(profile.id)}
                                                            title={i18n._('edit')}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-error btn-sm ml-2"
                                                            onClick={() => handleDeleteRequest(profile.id)}
                                                            title={i18n._('delete')}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {profiles.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center text-gray p-4">
                                                    <div className="empty">
                                                        <div className="empty-icon">
                                                            <i className="icon icon-people icon-3x" />
                                                        </div>
                                                        <p className="empty-title h5">
                                                            <Trans id="profiles:no_profiles" />
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {totalPages > 1 && (
                                    <div className="mt-4">
                                        <ArticlePagination
                                            currentPage={currentPage - 1}
                                            pages={totalPages}
                                            total={totalItems}
                                            onPageChange={onPageChange}
                                            itemTranslationKey="profiles:pagination:items"
                                            layout="compact"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default MatchWhenRole(withI18n()(ProfilesList), 'admin');
